import * as yup from 'yup'

import { FragmentType, gql, useFragment } from 'graphql-generated'
import { GetText } from 'lib/gettext'
import { Avatar, UserDisplayName } from 'common'

import Role from './role'
import Interests from './interests'

const PROFILE_FRAGMENT = gql(`
  fragment PublicProfile_Profile on auth_user {
    id
    first_name
    last_name
    username
    email @include(if: $withEmail)
    profile {
      occupation
      institution
      department
      area_of_research
      position
      websites
      location
      biography
      ...PublicProfile_Profile_Interests
    }
    ...PublicProfile_Profile_Role
  }
`)

type ProfileProps = {
  userRef: FragmentType<typeof PROFILE_FRAGMENT>
  className?: string
}

function deserializeWebsites(websites: unknown): ReadonlyArray<string> {
  return yup
    .array()
    .of(yup.string().required())
    .required()
    .validateSync(websites)
}

export default function Profile(props: ProfileProps) {
  const user = useFragment(PROFILE_FRAGMENT, props.userRef)

  const websites = user.profile?.websites
    ? deserializeWebsites(user.profile.websites)
    : []

  return (
    <div className={props.className}>
      <div className="mb-8 flex flex-row items-center gap-8">
        <Avatar size={128} userId={user.id} />

        <div className="w-full">
          <h1 className="text-3xl font-semibold">
            <UserDisplayName
              firstName={user.first_name}
              lastName={user.last_name}
              username={user.username}
            />
          </h1>

          {user.email && (
            <div>
              <a className="link" href={`mailto:${user.email}`}>
                {user.email}
              </a>
            </div>
          )}

          {websites.length > 0 && (
            <ul>
              {websites.map((website) => (
                <li
                  key={website}
                  className="inline [&:not(:last-child)]:after:content-[',_']"
                >
                  <a
                    className="link"
                    href={website}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {website}
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="mb-12 grid gap-4 md:grid-cols-2">
        {user.profile?.occupation && (
          <div>
            <div className="text-xs uppercase leading-tight text-secondary">
              <GetText>Occupation</GetText>
            </div>
            <div>{user.profile.occupation}</div>
          </div>
        )}

        {user.profile?.institution && (
          <div>
            <div className="text-xs uppercase leading-tight text-secondary">
              <GetText>Institution</GetText>
            </div>
            <div>{user.profile.institution}</div>
          </div>
        )}

        {user.profile?.department && (
          <div>
            <div className="text-xs uppercase leading-tight text-secondary">
              <GetText>Department</GetText>
            </div>
            <div>{user.profile.department}</div>
          </div>
        )}

        {user.profile?.area_of_research && (
          <div>
            <div className="text-xs uppercase leading-tight text-secondary">
              <GetText>Area of research</GetText>
            </div>
            <div>{user.profile.area_of_research}</div>
          </div>
        )}

        {user.profile?.position && (
          <div>
            <div className="text-xs uppercase leading-tight text-secondary">
              <GetText>Position</GetText>
            </div>
            <div>{user.profile.position}</div>
          </div>
        )}

        {user.profile?.location && (
          <div>
            <div className="text-xs uppercase leading-tight text-secondary">
              <GetText>Location</GetText>
            </div>
            <div>{user.profile.location}</div>
          </div>
        )}

        <div>
          <div className="text-xs uppercase leading-tight text-secondary">
            <GetText>Role at DRH</GetText>
          </div>
          <div>
            <Role userRef={user} />
          </div>
        </div>
      </div>

      {user.profile?.biography && (
        <div className="mb-12">
          <h3 className="mb-2 text-lg font-semibold capitalize">
            <GetText>Biography</GetText>
          </h3>
          <p>{user.profile.biography}</p>
        </div>
      )}

      {user.profile && (
        <Interests className="mb-12" profileRef={user.profile} />
      )}
    </div>
  )
}
